import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const UserContext = createContext();
const USER_STORAGE_KEY = 'userData';

const isLocalStorageAvailable = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
};

const safeLocalStorageGet = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return null;
  } catch (error) {
    console.error('localStorage get error:', error);
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage set error:', error);
    return false;
  }
};

const safeLocalStorageRemove = (key) => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
      return true;
    }
    return false;
  } catch (error) {
    console.error('localStorage remove error:', error);
    return false;
  }
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const storedUser = safeLocalStorageGet(USER_STORAGE_KEY);
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('User initialization error:', error);
      return null;
    }
  });

  useEffect(() => {
    try {
      if (user) {
        safeLocalStorageSet(USER_STORAGE_KEY, JSON.stringify(user));
      } else {
        safeLocalStorageRemove(USER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('User storage error:', error);
    }
  }, [user]);

  const updateUser = useCallback((newUserData) => {
    setUser(newUserData);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    setUser: updateUser
  }), [user, updateUser]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
